import { createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"
import { SITE_TIME_ZONE, addDays, todayInZone } from "./lib/siteTime"
import type { CalendarDate, MinuteOfDay } from "./lib/siteTime"
import { stepWeekday } from "./lib/staffDay"
import type { StaffCalendarView } from "./lib/staffDay"
import type { SlotType } from "./lib/slotTypes"

/**
 * The open slot an administrator has clicked in order to book somebody into it.
 *
 * Identifies the cell rather than carrying it: the grid is rebuilt whenever the day or team changes,
 * so a copy of the cell kept here would be the one thing on screen that had stopped agreeing with
 * Cal. Everything below survives that rebuild.
 */
export interface NewBookingTarget {
  /** The grid column, so the dialog can name the host without searching for them twice. */
  columnId: string
  /** Cal's id for that host. What the booking is actually pinned to. */
  calUserId: number
  /** The day the slot is on, team-local — supplied per target because the week view's columns differ. */
  date: CalendarDate
  startMinutes: MinuteOfDay
  /**
   * Which kind of appointment the cell is bookable as (AB#779). A free row is not simply free: it is
   * free for one kind of appointment, and `SlotData.slotType` is what says which. The event type slug
   * it books is derived from this at submit — `eventTypeSlugFor`.
   */
  slotType: SlotType
}

// ── What lives here, and what does not ────────────────────────────────
// This slice holds only what the user is looking at: which day, which team, which appointment is
// open in the detail panel.
//
// The grid is not kept here. It is derived from the real endpoints by useTeamDay and rebuilt when
// the day or team changes, so there is no second copy to fall out of date with Cal. What those
// endpoints returned is kept, in RTK Query's cache, for the session — so a day already read draws
// straight from it, and only the toolbar's refresh or one of this app's own writes reads it again.

export interface BookingCalendarState {
  /** The day on the grid, as a team-local calendar date, "YYYY-MM-DD". */
  selectedDate: CalendarDate

  /** Which team the admin is looking at, by Cal team id. Null until the first team loads. */
  selectedTeamId: number | null

  /**
   * Which of their own sites a staff member is looking at, by Cal team id. Null means all of them,
   * which is a choice rather than an absence — deliberately not the same null as `selectedTeamId`,
   * where it means "nothing picked yet, fall back to the first team".
   */
  selectedSiteId: number | null

  /**
   * The one staff member an administrator has opened the week of, by Cal user id. Null for the
   * team's day, which is what the administrator's calendar otherwise is.
   *
   * The id and not the person: the roster it was picked from is already loaded, and a name copied in
   * here would be a second one to go stale when Cal's changes.
   */
  viewingStaffCalUserId: number | null

  /** The appointment open in the detail panel, by Cal booking uid. */
  openBookingUid: string | null

  /**
   * The overlapping appointments listed for picking, by Cal booking uid, or null when no list is
   * open. Kept while one of them is open, so closing it goes back to the list it was picked from.
   *
   * Uids and not bookings, for the same reason as `openBookingUid`: the grid is rebuilt under the
   * list, and one cancelled from it should drop out of it rather than linger as a stale copy.
   */
  overlapBookingUids: string[] | null

  /** The open slot being booked into, or null when nobody is booking one. */
  newBookingTarget: NewBookingTarget | null

  /**
   * Whether the staff calendar draws the whole week or one day of it (AB#766). The day is
   * `selectedDate`, so switching keeps the place: the week view opens on the week holding the day,
   * and the day view on the day the week was anchored to.
   */
  staffView: StaffCalendarView

  /**
   * The Pacific today on which `selectedDate` was last chosen: when this module loaded, when the
   * calendar was last opened on a new day, on "Go to today", or when the user last moved the date
   * (AB#821).
   *
   * What keeps a tab left open overnight from opening yesterday: the calendar compares it with today
   * on mount (`openCalendar`), and a selection made on an earlier day gives way to the new today. A day
   * picked today — even one picked after midnight in a tab loaded the day before — stays picked across
   * pages until the date changes.
   */
  todayWhenOpened: CalendarDate
}

// The office's today, not the browser's and not UTC's: after 5pm Pacific
// `new Date().toISOString()` is already tomorrow, which would open the calendar on the wrong day.
// Only a starting point: the module loads once per tab, and `openCalendar` moves it on when the
// calendar is next opened on a later day.
const loadedOn = todayInZone(SITE_TIME_ZONE)

const initialState: BookingCalendarState = {
  selectedDate: loadedOn,
  selectedTeamId: null,
  selectedSiteId: null,
  viewingStaffCalUserId: null,
  openBookingUid: null,
  overlapBookingUids: null,
  newBookingTarget: null,
  staffView: "week",
  todayWhenOpened: loadedOn,
}

/**
 * Whether the calendar, opened on `today` (Pacific), would show a selection made on an earlier day —
 * the tab was left open past midnight (AB#821). `openCalendar` is the fix; this is what decides it.
 */
export function selectionIsStale(state: Pick<BookingCalendarState, "todayWhenOpened">, today: CalendarDate): boolean {
  return state.todayWhenOpened !== today
}

/**
 * A move of the selected date and the Pacific today it was made on (AB#821). `today` is stamped by
 * the action creator's `prepare`, not read in the reducer, so the reducer is a pure function of its
 * action; a caller may pass one, as the tests do.
 */
interface DateMove {
  /** Days (`navDay`) or working days (`navStaffDay`) to step; negative steps back. */
  delta: number
  /** The Pacific today the move was made on — what `todayWhenOpened` records. */
  today: CalendarDate
}

/** A date picked outright, and the Pacific today it was picked on (AB#821). See `DateMove`. */
interface DatePick {
  date: CalendarDate
  today: CalendarDate
}

const bookingCalendarSlice = createSlice({
  name: "bookingCalendar",
  initialState,
  reducers: {
    // Moving the date is the whole of it: the new day is a new fetch window, so the data follows.
    // `navDay(delta)`; the Pacific today the move was made on is stamped when the action is created,
    // so the reducer stays pure (AB#821).
    navDay: {
      reducer(state, action: PayloadAction<DateMove>) {
        state.selectedDate = addDays(state.selectedDate, action.payload.delta)
        state.todayWhenOpened = action.payload.today
      },
      prepare: (delta: number, today: CalendarDate = todayInZone(SITE_TIME_ZONE)) => ({ payload: { delta, today } }),
    },
    /**
     * The staff day view's arrows: a working day at a time, skipping the weekend the grid does not
     * draw — see `lib/staffDay`. `navStaffDay(delta)`, with today stamped as `navDay` stamps it.
     */
    navStaffDay: {
      reducer(state, action: PayloadAction<DateMove>) {
        state.selectedDate = stepWeekday(state.selectedDate, action.payload.delta)
        state.todayWhenOpened = action.payload.today
      },
      prepare: (delta: number, today: CalendarDate = todayInZone(SITE_TIME_ZONE)) => ({ payload: { delta, today } }),
    },
    /** Week or day. Nothing else changes: the date, the site and any open appointment all stay. */
    setStaffView(state, action: PayloadAction<StaffCalendarView>) {
      state.staffView = action.payload
    },
    /** `setSelectedDate(date)`, with today stamped as `navDay` stamps it. */
    setSelectedDate: {
      reducer(state, action: PayloadAction<DatePick>) {
        state.selectedDate = action.payload.date
        state.todayWhenOpened = action.payload.today
      },
      prepare: (date: CalendarDate, today: CalendarDate = todayInZone(SITE_TIME_ZONE)) => ({ payload: { date, today } }),
    },
    goToday(state) {
      const today = todayInZone(SITE_TIME_ZONE)
      state.selectedDate = today
      state.todayWhenOpened = today
    },
    /**
     * The calendar has been opened (mounted) on `today`, the Pacific date (AB#821). A selection made
     * on an earlier day — the tab was left open past midnight — moves to today; one made today is
     * kept, so leaving the calendar and coming back the same day stays where it was.
     */
    openCalendar(state, action: PayloadAction<CalendarDate>) {
      if (!selectionIsStale(state, action.payload)) return
      state.selectedDate = action.payload
      state.todayWhenOpened = action.payload
    },
    setSelectedTeam(state, action: PayloadAction<number>) {
      state.selectedTeamId = action.payload
    },
    /** Null for all of their sites at once. */
    setSelectedSite(state, action: PayloadAction<number | null>) {
      state.selectedSiteId = action.payload
    },

    /**
     * Opens one member of the team on screen as their own week.
     *
     * The site comes with them, so the week opens at the office the administrator was already
     * looking at rather than merged across every office that person works at. Any open appointment
     * closes: its uid belongs to the day grid being left behind, and the week has no such cell to
     * put back on screen.
     */
    viewStaffWeek(state, action: PayloadAction<{ calUserId: number; siteId: number | null }>) {
      state.viewingStaffCalUserId = action.payload.calUserId
      state.selectedSiteId = action.payload.siteId
      state.openBookingUid = null
      state.overlapBookingUids = null
      state.newBookingTarget = null
    },

    /** Back to the team's day. Same reasoning as above for the open appointment. */
    exitStaffWeek(state) {
      state.viewingStaffCalUserId = null
      state.openBookingUid = null
      state.overlapBookingUids = null
      state.newBookingTarget = null
    },

    /** Leaves any overlap list open underneath, so closing this appointment returns to it. */
    openBooking(state, action: PayloadAction<string>) {
      state.openBookingUid = action.payload
    },
    closeBooking(state) {
      state.openBookingUid = null
    },

    /** Lists the appointments competing for one row, for the user to pick which to open. */
    openOverlap(state, action: PayloadAction<string[]>) {
      state.overlapBookingUids = action.payload
      state.openBookingUid = null
    },
    closeOverlap(state) {
      state.overlapBookingUids = null
    },

    /**
     * Opens the booking form on one open slot.
     *
     * Closes any appointment being read at the same time: the two dialogs answer different questions
     * about the same grid, and both on screen at once would be a stack rather than a choice.
     */
    openSlotBooking(state, action: PayloadAction<NewBookingTarget>) {
      state.newBookingTarget = action.payload
      state.openBookingUid = null
      state.overlapBookingUids = null
    },
    closeSlotBooking(state) {
      state.newBookingTarget = null
    },
  },
})

export const {
  navDay,
  navStaffDay,
  setStaffView,
  setSelectedDate,
  goToday,
  openCalendar,
  setSelectedTeam,
  setSelectedSite,
  viewStaffWeek,
  exitStaffWeek,
  openBooking,
  closeBooking,
  openOverlap,
  closeOverlap,
  openSlotBooking,
  closeSlotBooking,
} = bookingCalendarSlice.actions

export default bookingCalendarSlice.reducer
